/**
 * The calls behind portal links (docs/integration-portal-link-proposal.md §5.2 in the SVN repo).
 *
 * The server acts on the workspace named by the shared client's wsId header, as every other
 * workspace call does. Errors are { error, message } with an HTTP status; a 404 { error:
 * "not_enabled" } means the feature is off on that server.
 *
 * The launch is the one call made from outside a shell (the bare /portal/:id route), where no
 * workspace has been loaded and the shared client carries no wsId yet. It names the workspace
 * itself, from the cookie or local storage the shells write when a workspace opens.
 */
import { commonStore } from "../store/commonStore";
import LocalStorageService from "../store/LocalStorageService";
import webAppConfig from "../commons/webApp.config";

function client() {
  return commonStore.state.client;
}

function path(id, rest) {
  return "/api/portal/" + encodeURIComponent(String(id)) + (rest || "");
}

/** Settings: { allowedOrigins, portals: [admin view] }. Workspace admins only. */
export function listPortals() {
  return client().get("/api/portal");
}

/** The address rules plus the page's opt-in, for the form: { ok, url } or a 400 with a message. */
export function checkPortalAddress(url) {
  return client().post("/api/portal/check", { url });
}

/** { name, icon, url, visibility, showOnWeb, showOnApp, enabled } -> the new portal's admin view. */
export function createPortal(fields) {
  return client().post("/api/portal", fields);
}

/** Only the fields sent change. A new address, a narrower audience or switching off ends open sessions. */
export function updatePortal(id, fields) {
  return client().put(path(id), fields);
}

/** -> { id, deleted, endedSessions } */
export function deletePortal(id) {
  return client().delete(path(id));
}

/** "End open sessions" -> { id, endedSessions } */
export function endPortalSessions(id) {
  return client().post(path(id, "/sessions/revoke"), {});
}

/**
 * The workspace a fresh tab is in: the cookie the shells set when a workspace opens, else the copy
 * they keep in local storage. Null when neither is there.
 */
export function currentWorkspaceId() {
  let id = null;
  try {
    const cookies = typeof window !== "undefined" ? window.$cookies : null;
    id = cookies && typeof cookies.get === "function" ? cookies.get("wsId") : null;
  } catch (err) {
    id = null;
  }
  if (!id) {
    try {
      id = LocalStorageService.getWorkspaceId(webAppConfig.baseServer);
    } catch (err) {
      id = null;
    }
  }
  return id && id !== "null" && id !== "undefined" ? String(id) : null;
}

function workspaceConfig(workspaceId) {
  return workspaceId ? { headers: { wsId: workspaceId } } : {};
}

/**
 * A member's click: -> { url, expiresAt }. The url carries a single-use code in its fragment.
 *
 * `context` is { moduleId, activityId, entryId? } when an activity opens this portal instead of its
 * standard form (docs/integration-portal-claude-design-proposal.md §3.6, §3.8). The server checks
 * that the activity is set to this portal and that the member may perform it, and binds the
 * context to the session. Without one, the launch is a menu launch, as before.
 */
export function launchPortal(id, workspaceId, context) {
  const body = context ? { platform: "web", context } : { platform: "web" };
  return client().post(path(id, "/launch"), body, workspaceConfig(workspaceId));
}

/**
 * The debug launch (§3.4): this admin's own session for the workspace goes to
 * http://localhost:{port}/ on their own computer, for `hours` (1, 2, 4 or 8).
 * -> { url, expiresAt, grant: { id, expiresAt } }. Like the launch, it runs from the bare
 * /portal/debug route, so it names its workspace itself.
 */
export function debugLaunch({ port, hours, context, workspaceId } = {}) {
  const body = { port, hours, platform: "web" };
  if (context) body.context = context;
  return client().post("/api/portal/debug/launch", body, workspaceConfig(workspaceId));
}

/** Settings: -> { grants: [{ id, userId, userName, origin, createdAt, expiresAt, mine }] }, the live ones. */
export function listDebugGrants() {
  return client().get("/api/portal/debug/grants");
}

/** "End debug" -> { id, endedSessions } */
export function endDebugGrant(id) {
  return client().post("/api/portal/debug/grants/" + encodeURIComponent(String(id)) + "/end", {});
}

export default {
  listPortals,
  checkPortalAddress,
  createPortal,
  updatePortal,
  deletePortal,
  endPortalSessions,
  currentWorkspaceId,
  launchPortal,
  debugLaunch,
  listDebugGrants,
  endDebugGrant,
};
